export type Query = Record<string, string | number | boolean | undefined>;

export interface ClientOptions {
  baseUrl: string; // should include /api
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  headers?: Record<string, string>;
  query?: Query;
  body?: unknown;
  keepalive?: boolean;
  token?: string | null;
  timeoutMs?: number;
}

function buildQuery(query?: Query): string {
  if (!query) return "";
  const params = Object.entries(query)
    .filter(([, v]): boolean => v !== undefined && v !== "")
    .map(
      ([k, v]): string =>
        `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
    )
    .join("&");
  return params ? `?${params}` : "";
}

async function request<T>(
  baseUrl: string,
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { ...(opts.headers || {}) };
  // Set Content-Type only when we actually send a body to avoid unnecessary preflights
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;

  const url = `${baseUrl}${path}${buildQuery(opts.query)}`;

  const timeoutMs = opts.timeoutMs ?? 10000;
  const controller = new AbortController();
  const timeoutId = setTimeout((): void => controller.abort(), timeoutMs);
  try {
    // Step 1: Use fetch to get data

    const response = await fetch(url, {
      method: opts.method || "GET",
      headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      signal: controller.signal,
      keepalive: opts.keepalive,
    });

    // Step 2: Parse data immediately
    let data: T;
    if (response.status === 204) {
      data = undefined as unknown as T;
    } else if (response.status >= 400) {
      const contentType = response.headers.get("content-type");

      if (contentType?.includes("application/json")) {
        const payload: unknown = await response.json();
        const errorData: Record<string, unknown> =
          payload !== null && typeof payload === "object"
            ? (payload as Record<string, unknown>)
            : {};

        // Extract human-readable message from error response
        const errorMessage =
          typeof errorData.error === "string"
            ? errorData.error
            : typeof errorData.message === "string"
              ? errorData.message
              : "An error occurred";
        const errorCode = errorData.code;

        // Create error object with structured data
        const error = new Error(errorMessage) as Error & {
          code?: string;
          status: number;
          waitSeconds?: number;
          limitStatus?: unknown;
          payload?: unknown;
        };
        error.status = response.status;
        if (typeof errorCode === "string") error.code = errorCode;
        if (typeof errorData.waitSeconds === "number")
          error.waitSeconds = errorData.waitSeconds;
        if (errorData.limitStatus) error.limitStatus = errorData.limitStatus;
        error.payload = errorData;

        throw error;
      } else {
        const errorText = await response.text();
        throw new Error(
          errorText || `Request failed with status ${response.status}`,
        );
      }
    } else {
      data = (await response.json()) as T;
    }

    // Step 3: Return the data directly
    return data;
  } catch (err: unknown) {
    // Handle AbortError specifically
    if (
      err !== null &&
      typeof err === "object" &&
      "name" in err &&
      err.name === "AbortError"
    ) {
      throw Object.assign(new Error("Request timed out"), {
        code: "REQUEST_TIMEOUT",
      });
    }
    if (err instanceof TypeError) {
      throw Object.assign(new Error("Could not reach the server"), {
        code: "NETWORK_ERROR",
      });
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export { request };
